import { useMediaQuery } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../../../global-state/themeContext';
import { Icon, Icons, Tooltip, Typography } from '../../../stories/atoms';
import { ColumnFlexContainer, RowFlexContainer } from '../../../stories/atoms/containerV2/ContainerV2';
import { muiTheme } from '../../../theme';
import { commonTranslations, homeTranslations } from '../../../translations';
import { formatAmountWithSeparator } from '../../../utils';
import { StyledButton, StyledTypography } from './SummaryWidget.styles';

export type SummaryWidgetType = {
  id: string;
  title: string;
  description: string;
  headerIcon: Icons;
  value: number;
  isLoading: boolean;
  error: boolean;
  retryHandler: () => void;
};

export const SummaryWidget: FC<SummaryWidgetType> = ({
  id,
  title,
  description,
  headerIcon,
  value,
  isLoading,
  error,
  retryHandler,
}) => {
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));
  const { mode } = useThemeContext();
  const { t } = useTranslation();

  const backgroundColor = mode === 'dark' ? 'backgroundPaperDark' : 'white';
  if (isLoading && !error) {
    return (
      <ColumnFlexContainer
        key={id}
        backgroundColor={backgroundColor}
        shadow
        borderRadius={[2]}
        height={isMobile ? [18] : [24]}
        justifyContent="center"
        alignItems="center"
        gap={[2]}
        data-testid="loading-area-view"
      >
        <Icon icon={Icons.SPINNER} size="small" />
        <Typography type="body2">
          {t(homeTranslations.common.loading.key, homeTranslations.common.loading.defaultValue)}...
        </Typography>
      </ColumnFlexContainer>
    );
  }

  if (error && !isLoading) {
    return (
      <ColumnFlexContainer
        key={id}
        backgroundColor={backgroundColor}
        shadow
        borderRadius={[2]}
        height={isMobile ? [18] : [24]}
        justifyContent="center"
        alignItems="center"
        gap={[2]}
        data-testid="error-area-view"
      >
        <Typography type="body2">
          {t(commonTranslations.errorText1.key, commonTranslations.errorText1.defaultValue)}
        </Typography>
        <StyledButton
          label={t(commonTranslations.retry.key, commonTranslations.retry.defaultValue)}
          onClick={retryHandler}
          buttonContentProps={{
            iconOptions: { customIcon: <Icon icon={Icons.REFRESH} size="small" /> },
          }}
        />
      </ColumnFlexContainer>
    );
  }

  return (
    <ColumnFlexContainer
      key={id}
      shadow
      backgroundColor={backgroundColor}
      borderRadius={[2]}
      padding={[3, 4]}
      gap={[3]}
      alignItems={isMobile ? 'center' : undefined}
      data-testid={`${id}-wrapper`}
    >
      {isMobile ? (
        <>
          <Tooltip
            text={
              <>
                <Typography type="body4" color="white">
                  {title}:
                </Typography>
                <br />
                <Typography type="caption" color="white">
                  {description}
                </Typography>
              </>
            }
            position="top"
            arrowedToolTip
          >
            <Icon icon={headerIcon} size="small" />
          </Tooltip>
          <Typography type="h4">{formatAmountWithSeparator(value)}</Typography>
        </>
      ) : (
        <>
          <StyledTypography type="caption" color="textGray">
            {title}
          </StyledTypography>
          <RowFlexContainer justifyContent="between" alignItems="center">
            <Typography type="h4">{formatAmountWithSeparator(value)}</Typography>
            <Icon icon={headerIcon} size="small" />
          </RowFlexContainer>
        </>
      )}
    </ColumnFlexContainer>
  );
};
